"use client";

import { Mail, Instagram, Globe } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function IrisMark({
  className = ""
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.25" className={className} aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="5" />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
    </svg>;
}
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <ul className="flex flex-col gap-3">
        {[0, 1, 2, 3, 4].map(i => <li key={i} data-index={i}>
            <div className="h-4 w-24 bg-muted animate-pulse rounded-md" />
          </li>)}
      </ul>;
  }
  return <ul className="flex flex-col gap-3">
      {menuItems.map((item, i) => <li key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="text-foreground font-default text-sm tracking-wide relative inline-block group">
            <span className="relative">
              {item.label}
              <span className="absolute left-0 right-0 h-px bg-primary transition-all duration-300 group-hover:h-0.5 group-hover:right-[-8px]" style={{
            bottom: "-6px"
          }} />
            </span>
          </Link>
        </li>)}
    </ul>;
}
const tickerText = "WELCOME BABY OHLSON  ·  CELEBRATING NEW LIFE  ·  EST. 2024  ·  A NEW CHAPTER BEGINS  ·  ";
const socials = [{
  label: "Instagram",
  href: "https://instagram.com",
  icon: "instagram"
}, {
  label: "Registry",
  href: "#",
  icon: "globe"
}, {
  label: "RSVP Email",
  href: "mailto:hello@ohlsonfamily.com",
  icon: "mail"
}];
export default function Footer() {
  return <section data-section-id="1798" id="footer" className="text-foreground pt-24 pb-12 relative overflow-hidden light bg-muted">
      {}
      <div className="absolute top-0 left-0 right-0 flex items-center">
        <span className="w-2 h-2 bg-primary -ml-1" />
        <span className="flex-1 h-px bg-primary" />
        <span className="w-2 h-2 bg-primary -mr-1" />
      </div>

      {}
      <div className="relative overflow-hidden border-b border-border group">
        <div className="flex whitespace-nowrap py-5 group-hover:[animation-play-state:paused]" style={{
        animation: "footer-ticker 40s linear infinite"
      }}>
          {[0, 1, 2, 3].map(rep => <div key={rep} data-index={rep} className="flex items-center font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground shrink-0">
              {tickerText.split("·").map((chunk, i) => <span key={i} data-index={i} className="flex items-center">
                  <span className="px-4">{chunk.trim()}</span>
                  <IrisMark className="w-3 h-3 text-primary shrink-0" />
                </span>)}
            </div>)}
        </div>
        <style>{`
          @keyframes footer-ticker {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
        `}</style>
      </div>

      {}
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 pt-20 pb-12">
        {}
        <div className="font-mono text-[0.6875rem] tracking-[0.2em] uppercase text-muted-foreground mb-12">§ 04 — ARCHIVE · OHLSON FAMILY 2026</div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-6">
          {}
          <div className="md:col-span-4 flex flex-col gap-8">
            <div className="flex items-center gap-3">
              <Link to="/" className="flex items-center gap-3">
                <WvcLogo className="h-7 w-auto" />
              </Link>
              <IrisMark className="w-6 h-6 text-primary" />
            </div>

            <div className="flex flex-col gap-5">
              <p className="font-serif italic text-lg md:text-xl leading-relaxed text-foreground/95">
                The Ohlson Residence
              </p>
              <div className="flex items-center gap-3">
                <span className="w-10 h-px bg-primary" />
                <span className="w-1 h-1 bg-primary rounded-md" />
              </div>
              <p className="font-mono text-xs tracking-[0.12em] uppercase text-muted-foreground leading-relaxed">
                Sunday · 11:00–14:00
                <br />
                Brunch & Celebration
              </p>
              <p className="font-mono text-xs tracking-[0.12em] uppercase text-muted-foreground">
                RSVP by October 15th
              </p>
            </div>
          </div>

          {}
          <div className="md:col-span-4 md:pl-8 flex flex-col gap-6">
            <div className="font-mono text-[0.6875rem] tracking-[0.2em] uppercase text-muted-foreground">
              Navigation
            </div>
            <MenuProvider menu_id="17">
              <FooterMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="md:col-span-4 flex flex-col gap-6">
            <div className="font-mono text-[0.6875rem] tracking-[0.2em] uppercase text-muted-foreground">
              Digital Space
            </div>
            <ul className="flex flex-col gap-4">
              {socials.map((s, i) => <li key={s.label} data-index={i}>
                  <Link to={s.href} newTab className="text-foreground font-default text-sm tracking-wide inline-flex items-center gap-3 group">
                    {s.icon === "instagram" && <Instagram className="w-4 h-4 stroke-[1.25] text-foreground group-hover:text-primary transition-colors duration-300" />}
                    {s.icon === "globe" && <Globe className="w-4 h-4 stroke-[1.25] text-foreground group-hover:text-primary transition-colors duration-300" />}
                    {s.icon === "mail" && <Mail className="w-4 h-4 stroke-[1.25] text-foreground group-hover:text-primary transition-colors duration-300" />}
                    <span className="relative">
                      {s.label}
                      <span className="absolute left-0 right-0 h-px bg-primary transition-all duration-300 group-hover:h-0.5 group-hover:right-[-8px]" style={{
                    bottom: "-6px"
                  }} />
                    </span>
                  </Link>
                </li>)}
              <li data-index={3}>
                <Link to="mailto:hello@ohlsonfamily.com" className="text-muted-foreground font-mono text-xs tracking-[0.12em] uppercase hover:text-primary transition-colors duration-300">
                  hello@ohlsonfamily.com
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {}
        <div className="mt-20 flex items-center">
          <span className="w-2 h-2 bg-primary rounded-md" />
          <span className="flex-1 h-px bg-primary" />
          <span className="w-2 h-2 bg-primary rounded-md" />
        </div>

        {}
        

        {}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <p className="font-mono text-[0.6875rem] tracking-[0.2em] uppercase text-muted-foreground">© 2026&nbsp;WELCOME BABY OHLSON · ORGANIC ARCHIVE</p>
          <ul className="flex items-center gap-6 md:gap-8">
            {[{
            label: "Registry",
            to: "#"
          }, {
            label: "Privacy",
            to: "#"
          }, {
            label: "Contact",
            to: "mailto:hello@ohlsonfamily.com"
          }].map((item, i) => <li key={item.label} data-index={i}>
                <Link to={item.to} className="text-muted-foreground font-mono text-[0.6875rem] tracking-[0.2em] uppercase hover:text-primary transition-colors duration-300">
                  {item.label}
                </Link>
              </li>)}
          </ul>
        </div>
      </div>
    </section>;
}